import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { SharedProjectView, SharedRecommendedDesign, SharedWall } from '../../../services';
import { ShareBrandFooter } from './ShareBrandFooter.component';
import { ShareBrandHeader } from './ShareBrandHeader.component';
import { wallIsUnfulfillable } from './ShareWallSection.component';

// #region ShareReviewStepProps
export interface ShareReviewStepProps {
    project: SharedProjectView;
    // Client-side picks — the review reflects exactly what is on screen, not
    // whatever the last fetch happened to return.
    selections: Record<number, number[]>;
    confirming: boolean;
    onBack: () => void;
    // Jump back to a specific wall to change its pick.
    onEditWall: (wallId: number) => void;
    onConfirm: () => void;
}

// #region ReviewImage
const ReviewImage: React.FunctionComponent<{ label: string; src: string | null; alt: string; fallback: string }> = (props): JSX.Element => (
    <div className="flex flex-col gap-1.5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-share-muted">{props.label}</div>
        <div className="relative aspect-square overflow-hidden rounded-2xl border border-share-border bg-share-surface">
            {props.src ? (
                <img src={props.src} alt={props.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-center" />
            ) : (
                <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs text-share-muted">
                    {props.fallback}
                </div>
            )}
        </div>
    </div>
);

// #region ReviewWallCard
const ReviewWallCard: React.FunctionComponent<{
    wall: SharedWall;
    designId: number | undefined;
    onEdit: () => void;
}> = (props): JSX.Element => {
    const { wall, designId, onEdit } = props;

    const chosen: SharedRecommendedDesign | undefined = wall.recommended_designs.find(
        (design: SharedRecommendedDesign) => design.design_id === designId
    );
    const mockupReady = chosen?.mockup_status === 'GENERATED' && !!chosen.ai_mockup_signed_url;

    return (
        <section className="rounded-3xl border border-share-border bg-share-raised p-4 shadow-sm sm:p-6">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="truncate text-lg font-semibold tracking-tight text-share-text">{wall.name}</h2>
                    <div className="truncate text-sm text-share-muted">{chosen?.title || 'No selection'}</div>
                </div>
                <button
                    type="button"
                    onClick={onEdit}
                    className="min-h-[40px] shrink-0 rounded-xl px-3 text-sm font-semibold text-share-primary transition hover:bg-share-primary-soft"
                >
                    Change
                </button>
            </div>

            {chosen && (
                <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4">
                    <ReviewImage
                        label="Design"
                        src={chosen.thumbnail_url ?? null}
                        alt={chosen.title || 'Untitled'}
                        fallback="No preview"
                    />
                    <ReviewImage
                        label="On your wall"
                        src={mockupReady ? (chosen.ai_mockup_signed_url as string) : null}
                        alt={`${chosen.title || 'Design'} on your wall`}
                        fallback={chosen.mockup_status === 'FAILED' ? 'Preview unavailable' : 'Preview still generating'}
                    />
                </div>
            )}
        </section>
    );
};

// #region ShareReviewStep
// Second step of the flow: the client reviews every pick together before
// anything is locked in. Reached from the "Review" action, and every wall can
// still be changed from here.
export const ShareReviewStep: React.FunctionComponent<ShareReviewStepProps> = (props: ShareReviewStepProps): JSX.Element => {
    const { project, selections, confirming, onBack, onEditWall, onConfirm } = props;

    const walls = project.walls.filter((wall: SharedWall) => !wallIsUnfulfillable(wall));

    return (
        <div className="space-y-5 sm:space-y-6">
            <ShareBrandHeader
                branding={project.branding}
                clientName={project.client_name}
                headlineOverride="Review your selections"
                bodyOverride="Here's everything you picked. Change anything that isn't quite right — once you confirm, your designer is notified and your picks are locked in."
            />

            <button
                type="button"
                onClick={onBack}
                className="flex min-h-[44px] items-center gap-2 text-sm font-semibold text-share-primary transition hover:opacity-80"
            >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to designs
            </button>

            {walls.map((wall: SharedWall) => (
                <ReviewWallCard
                    key={wall.id}
                    wall={wall}
                    designId={(selections[wall.id] ?? [])[0]}
                    onEdit={() => onEditWall(wall.id)}
                />
            ))}

            <ShareBrandFooter branding={project.branding} />

            <div
                className="fixed inset-x-0 bottom-0 z-30 border-t border-share-border bg-share-raised backdrop-blur"
            >
                <div
                    className="mx-auto flex max-w-3xl items-center gap-3 px-4 pt-3 sm:px-6"
                    style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
                >
                    <button
                        type="button"
                        onClick={onBack}
                        disabled={confirming}
                        className="min-h-[48px] shrink-0 rounded-xl px-4 text-sm font-semibold text-share-muted transition hover:bg-share-surface disabled:opacity-50"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={confirming}
                        style={{ backgroundColor: 'var(--share-primary)', color: 'var(--share-primary-contrast)' }}
                        className="min-h-[48px] flex-1 rounded-xl px-5 text-sm font-semibold shadow-sm transition hover:brightness-110 disabled:opacity-60"
                    >
                        {confirming ? 'Confirming...' : 'Confirm selections'}
                    </button>
                </div>
            </div>
        </div>
    );
};
